/**
 * Premium Theme with Exact Color Scheme
 * Colors from provided color scheme
 */

import { createTheme } from '@mui/material/styles';

// Exact color palette from color scheme
const colors = {
  // Primary: Bright fuchsia pink (Row 1, Swatch 1)
  primary: {
    main: '#FF06CD', // RGB(255, 6, 205)
    light: '#FF4DD9',
    dark: '#CC05A3',
    contrastText: '#ffffff',
  },
  // Secondary: Deep purple (Row 1, Swatch 2)
  secondary: {
    main: '#6200B6', // RGB(98, 0, 182)
    light: '#7D33C4',
    dark: '#4D0091',
    contrastText: '#ffffff',
  },
  // Accent colors from Row 2
  accent: {
    blueGrey: '#7698B3', // RGB(118, 152, 179)
    orange: '#FF8400', // RGB(255, 132, 0)
    teal: '#09BFC9', // RGB(9, 191, 201)
    blue: '#0892D0', // RGB(8, 146, 208)
    darkIndigo: '#2D0363', // RGB(45, 3, 99)
    magenta: '#AA0993', // RGB(170, 9, 147)
  },
  // Success: Teal from accent colors
  success: {
    main: '#09BFC9', // RGB(9, 191, 201)
    light: '#33D1D9',
    dark: '#0799A1',
  },
  // Warning: Orange from accent colors
  warning: {
    main: '#FF8400', // RGB(255, 132, 0)
    light: '#FFA333',
    dark: '#CC6900',
  },
  // Error: Magenta from accent colors
  error: {
    main: '#AA0993', // RGB(170, 9, 147)
    light: '#C433B0',
    dark: '#880775',
  },
  // Info: Blue from accent colors
  info: {
    main: '#0892D0', // RGB(8, 146, 208)
    light: '#33A8D9',
    dark: '#0675A6',
  },
  // Neutrals from Row 3
  neutral: {
    darkBlueBlack: '#0C0023', // RGB(12, 0, 35)
    darkGrayPurple: '#645E7A', // RGB(100, 94, 122)
    lightLavender: '#CFCAE0', // RGB(207, 202, 224)
    lightGrayPurple: '#EDEAF4', // RGB(237, 234, 244)
  },
  // Grayscale from Row 4
  grey: {
    50: '#F5F5F5', // Very light gray
    100: '#E6E6E6', // RGB(230, 230, 230)
    200: '#D9D9D9', // RGB(217, 217, 217)
    300: '#A0A09F', // RGB(160, 160, 159)
    400: '#7698B3', // Blue-grey accent
    500: '#645E7A', // Dark gray purple
    600: '#404040', // RGB(64, 64, 64)
    700: '#2D0363', // Dark indigo
    800: '#6200B6', // Deep purple
    900: '#0C0023', // Dark blue-black
  },
};

// Premium theme with exact color scheme
export const enterpriseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    grey: colors.grey,
    background: {
      default: '#EDEAF4', // Light gray purple from Row 3
      paper: '#ffffff',
    },
    text: {
      primary: '#0C0023', // Dark blue-black for text
      secondary: '#645E7A', // Dark gray purple
    },
    divider: '#CFCAE0', // Light lavender divider
  },
  typography: {
    // Apple's SF Pro font stack
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"SF Pro Display"',
      '"SF Pro Text"',
      'Helvetica Neue',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      color: '#0C0023',
    },
    h2: {
      fontSize: '2.75rem',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
      color: '#0C0023',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: '#0C0023',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#0C0023',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#0C0023',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#0C0023',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#0C0023',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#645E7A',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12, // Apple's rounded corners
  },
  // Subtle shadows
  shadows: [
    'none',
    '0px 1px 3px rgba(12,0,35,0.05)',
    '0px 2px 6px rgba(12,0,35,0.06)',
    '0px 4px 12px rgba(12,0,35,0.08)',
    '0px 8px 16px rgba(12,0,35,0.1)',
    '0px 12px 24px rgba(12,0,35,0.12)',
    '0px 16px 32px rgba(12,0,35,0.14)',
    '0px 20px 40px rgba(12,0,35,0.16)',
    '0px 24px 48px rgba(12,0,35,0.18)',
    '0px 28px 56px rgba(12,0,35,0.2)',
    '0px 32px 64px rgba(12,0,35,0.22)',
    '0px 36px 72px rgba(12,0,35,0.24)',
    '0px 40px 80px rgba(12,0,35,0.26)',
    '0px 44px 88px rgba(12,0,35,0.28)',
    '0px 48px 96px rgba(12,0,35,0.3)',
    '0px 52px 104px rgba(12,0,35,0.32)',
    '0px 56px 112px rgba(12,0,35,0.34)',
    '0px 60px 120px rgba(12,0,35,0.36)',
    '0px 64px 128px rgba(12,0,35,0.38)',
    '0px 68px 136px rgba(12,0,35,0.4)',
    '0px 72px 144px rgba(12,0,35,0.42)',
    '0px 76px 152px rgba(12,0,35,0.44)',
    '0px 80px 160px rgba(12,0,35,0.46)',
    '0px 84px 168px rgba(12,0,35,0.48)',
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 28px',
          fontWeight: 500,
          fontSize: '0.9375rem',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(255, 6, 205, 0.3)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: colors.primary.dark,
            boxShadow: '0px 6px 16px rgba(255, 6, 205, 0.4)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: colors.neutral.lightLavender,
          color: '#0C0023',
          '&:hover': {
            borderColor: colors.primary.main,
            backgroundColor: `${colors.primary.main}08`,
            borderWidth: '1.5px',
          },
        },
        text: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 8px rgba(12,0,35,0.06)',
          border: '1px solid rgba(207, 202, 224, 0.5)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: '#ffffff',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(12,0,35,0.1)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 8px rgba(12,0,35,0.06)',
          backgroundColor: '#ffffff',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(12,0,35,0.06)',
        },
        elevation2: {
          boxShadow: '0px 4px 12px rgba(12,0,35,0.08)',
        },
        elevation3: {
          boxShadow: '0px 8px 16px rgba(12,0,35,0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 0px rgba(12,0,35,0.05)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(207, 202, 224, 0.5)',
          color: '#0C0023',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(207, 202, 224, 0.5)',
          boxShadow: 'none',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}12`,
            color: colors.primary.main,
            '&:hover': {
              backgroundColor: `${colors.primary.main}20`,
            },
            '& .MuiListItemIcon-root': {
              color: colors.primary.main,
            },
          },
          '&:hover': {
            backgroundColor: colors.neutral.lightGrayPurple,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#ffffff',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': {
              borderColor: colors.neutral.lightLavender,
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: colors.neutral.darkGrayPurple,
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: colors.primary.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8125rem',
          height: 28,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.neutral.lightGrayPurple,
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: '#0C0023',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.neutral.lightLavender}`,
          padding: '16px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.neutral.lightLavender,
        },
      },
    },
  },
});

// Dark theme variant
export const enterpriseDarkTheme = createTheme({
  ...enterpriseTheme,
  palette: {
    ...enterpriseTheme.palette,
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: '#0C0023', // Dark blue-black
      paper: '#2D0363', // Dark indigo
    },
    text: {
      primary: '#EDEAF4',
      secondary: '#CFCAE0',
    },
    divider: '#645E7A',
  },
});

// Export color palette for use in components
export const colorPalette = colors;
